import { http } from '@/lib/http';

/** Mirrors the backend's invoice status enum. */
export type InvoiceStatus = 'unpaid' | 'pending_confirmation' | 'paid';

/** Mirrors `repo.Invoice` in the vizitpro-api swagger spec. */
export interface InvoiceDto {
  id: string;
  job_id: string;
  /** The specific visit this invoice was issued for, or null for a job-level invoice. */
  visit_id: string | null;
  /** Denormalized from jobs, populated on the business-wide list. */
  job_title: string | null;
  customer_user_id: string;
  status: InvoiceStatus;
  subtotal: number | null;
  tax: number | null;
  discount: number | null;
  total_amount: number;
  /** "YYYY-MM-DD" or null. */
  due_date: string | null;
  cdn_url: string | null;
  sent_at: string | null;
  paid_at: string | null;
  /** The payment method actually used — claimed or via Stripe. Null until paid/claimed. */
  payment_method_id: string | null;
  /** Payment claim/confirm provenance — set once the recipient claims a non-Stripe payment. */
  payment_claimed_at: string | null;
  payment_claimed_by_name: string | null;
  payment_confirmed_by_user_id: string | null;
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Request body for `POST /businesses/{business_id}/jobs/{job_id}/invoices` —
 * mirrors `controller.createInvoiceRequest`. `total_amount` and
 * `payment_method_ids` are required. `payment_method_ids` selects which of the
 * business's configured payment methods this invoice offers — omit (or pass
 * []) to offer every active one. The spec also accepts `cdn_url` but the
 * client has no uploader yet, so it's omitted here.
 */
export interface CreateInvoiceRequest {
  total_amount: number;
  payment_method_ids: string[];
  /** Ties the invoice to a specific visit on the job. Omit for a job-level invoice. */
  visit_id?: string;
  subtotal?: number;
  tax?: number;
  discount?: number;
  due_date?: string;
}

/**
 * Request body for `PATCH .../invoices/{id}` — mirrors
 * `controller.updateInvoiceRequest`. Only unpaid invoices are editable
 * (server returns 409 otherwise — surface that toast rather than
 * pre-checking). `payment_method_ids`, when present, replaces the offered
 * set wholesale (`[]` means "offer every active one"); omit it to leave the
 * current selection untouched — `GET` doesn't echo back the offered set, so
 * the edit UI only sends this field when the owner actually changes it.
 */
export interface UpdateInvoiceRequest {
  total_amount?: number;
  payment_method_ids?: string[];
  subtotal?: number;
  tax?: number;
  discount?: number;
  due_date?: string;
}

export interface ListBusinessInvoicesFilters {
  status?: InvoiceStatus;
  customer_user_id?: string;
  job_id?: string;
}

/**
 * Response of `POST .../invoices/{id}/share-link` — mirrors
 * `controller.invoiceShareLinkResponse`. `url` is the full link to hand to
 * the customer (`{PUBLIC_BASE_URL}/i/{token}`, composed server-side); `token`
 * is the raw token on its own; `expires_at` is RFC 3339 UTC, ~5 days out.
 */
export interface InvoiceShareLinkDto {
  url: string;
  token: string;
  expires_at: string;
}

export function businessInvoicesQueryKey(
  businessId: string,
  filters: ListBusinessInvoicesFilters,
) {
  return ['invoices', businessId, filters] as const;
}

export function jobInvoicesQueryKey(businessId: string, jobId: string) {
  return ['jobs', businessId, jobId, 'invoices'] as const;
}

export function invoiceQueryKey(businessId: string, jobId: string, invoiceId: string) {
  return ['jobs', businessId, jobId, 'invoices', invoiceId] as const;
}

/**
 * Calls `GET /businesses/{business_id}/invoices` — every invoice across the
 * business's jobs. Caller must be an owner.
 */
export async function listBusinessInvoices(
  businessId: string,
  filters: ListBusinessInvoicesFilters = {},
): Promise<InvoiceDto[]> {
  const { data } = await http.get<InvoiceDto[]>(`/businesses/${businessId}/invoices`, {
    params: filters,
  });
  return data;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}/invoices`. */
export async function listJobInvoices(
  businessId: string,
  jobId: string,
  status?: InvoiceStatus,
): Promise<InvoiceDto[]> {
  const { data } = await http.get<InvoiceDto[]>(
    `/businesses/${businessId}/jobs/${jobId}/invoices`,
    { params: status ? { status } : undefined },
  );
  return data;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}/invoices/{id}`. */
export async function getJobInvoice(
  businessId: string,
  jobId: string,
  invoiceId: string,
): Promise<InvoiceDto> {
  const { data } = await http.get<InvoiceDto>(
    `/businesses/${businessId}/jobs/${jobId}/invoices/${invoiceId}`,
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/jobs/{job_id}/invoices`. Caller must
 * be the owner, or an assigned employee granted `create_invoices`.
 */
export async function createJobInvoice(
  businessId: string,
  jobId: string,
  body: CreateInvoiceRequest,
): Promise<InvoiceDto> {
  const { data } = await http.post<InvoiceDto>(
    `/businesses/${businessId}/jobs/${jobId}/invoices`,
    body,
  );
  return data;
}

/** Calls `PATCH /businesses/{business_id}/jobs/{job_id}/invoices/{id}`. */
export async function updateJobInvoice(
  businessId: string,
  jobId: string,
  invoiceId: string,
  body: UpdateInvoiceRequest,
): Promise<InvoiceDto> {
  const { data } = await http.patch<InvoiceDto>(
    `/businesses/${businessId}/jobs/${jobId}/invoices/${invoiceId}`,
    body,
  );
  return data;
}

/** Calls `POST .../invoices/{id}/send`. Marks the invoice sent (sets `sent_at`). */
export async function sendJobInvoice(
  businessId: string,
  jobId: string,
  invoiceId: string,
): Promise<InvoiceDto> {
  const { data } = await http.post<InvoiceDto>(
    `/businesses/${businessId}/jobs/${jobId}/invoices/${invoiceId}/send`,
  );
  return data;
}

/**
 * Calls `POST .../invoices/{id}/share-link` (empty body). Mints a fresh
 * signed, expiring link — revoking any prior active link for the invoice —
 * and marks the invoice sent. Caller must be the owner, or an assigned
 * employee granted `create_invoices`. The token is shown only once.
 */
export async function createInvoiceShareLink(
  businessId: string,
  jobId: string,
  invoiceId: string,
): Promise<InvoiceShareLinkDto> {
  const { data } = await http.post<InvoiceShareLinkDto>(
    `/businesses/${businessId}/jobs/${jobId}/invoices/${invoiceId}/share-link`,
  );
  return data;
}

/**
 * Calls `POST .../invoices/{id}/confirm-paid`. The sender's manual sign-off
 * that a claimed (non-Stripe) payment actually arrived — moves the invoice
 * `pending_confirmation` -> `paid`. 409 if the invoice isn't currently
 * pending confirmation. Caller must be the owner or an assigned employee
 * with `create_invoices`.
 */
export async function confirmInvoicePaid(
  businessId: string,
  jobId: string,
  invoiceId: string,
): Promise<InvoiceDto> {
  const { data } = await http.post<InvoiceDto>(
    `/businesses/${businessId}/jobs/${jobId}/invoices/${invoiceId}/confirm-paid`,
  );
  return data;
}
